

import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { showToast } from "../utils/customToast";
import "./Invoice.css";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { apiWithLog } from "../utils/apiWithLog";
import { SECURITY_KEY } from "../globals";
import DeleteConfirmModal from "../Component/DeleteModal/DeleteConfirmModal";
import PdfPreviewModal from "../Component/main_contant/DashboardComponents/PdfPreviewModal";
import { getFinancialYear, formatInvoiceNumber, formatDateToDMY } from "../utils/financialYearHelper";
import { downloadInvoice } from "../utils/dashboardPdfHelper";
import { TableRowsSkeleton } from "../Component/Skeleton";

const ViewInvoice = () => {
  const [invoices, setInvoices] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const navigate = useNavigate();

  const [bankInfo, setBankInfo] = useState(null);

  // Date helper functions for default filter range (1st of current month -> today)
  const getFirstDayOfMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}-01`;
  };

  const getTodayDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Filter & pagination state
  const [filterFromDate, setFilterFromDate] = useState(getFirstDayOfMonth);
  const [filterToDate, setFilterToDate] = useState(getTodayDate);
  const [filterPartyName, setFilterPartyName] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Delete modal state
  const [deleteTarget, setDeleteTarget] = useState(null); // invoice object to delete
  const [isDeleting, setIsDeleting] = useState(false);

  // Preview modal state
  const [previewModal, setPreviewModal] = useState({ open: false, data: null });

  const BASE_API_URL = process.env.REACT_APP_BASE_API_URL;
  const BASE_REST_API_URL = process.env.REACT_APP_BASE_REST_API_URL;

  const buildLogoUrl = (fileName) => {
    if (!fileName || fileName === "null") return null;
    const path = fileName.toString().trim();
    if (/^https?:\/\//i.test(path)) return path;
    let cleaned = path.replace(/^~[\\/]+/, "").replace(/\\/g, "/").replace(/^\/+/, "");
    const restRoot = (BASE_REST_API_URL || "").replace(/\/+$/, "");
    if (/^uploads\//i.test(cleaned)) {
      return `${restRoot.replace(/\/restApi$/i, "")}/${cleaned}`;
    }
    return `${restRoot}/${cleaned}`;
  };

  const fetchRestCompany = async (companyId) => {
    if (!companyId || !BASE_REST_API_URL) return {};

    const data = new URLSearchParams();
    data.append("SecurityKey", SECURITY_KEY);
    data.append("TableName", "company");
    data.append("WhereCondition", `CompID=${companyId}`);
    data.append("*", "*");

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const response = await fetch(`${BASE_REST_API_URL}/SelectData.aspx`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
        signal: controller.signal,
      });
      const json = await response.json();
      return json.Response?.[0] || {};
    } finally {
      clearTimeout(timeout);
    }
  };

  // Fetch invoices when component loads
  useEffect(() => {
    const company = JSON.parse(localStorage.getItem("selectedCompany"));
    setSelectedCompany(company);

    if (company?.id) {
      fetchInvoices(company.id);
    } else {
      showToast.error("Please select a company first!");
    }
  }, []);

  // Watch for selectedCompany changes in localStorage
  useEffect(() => {
    const interval = setInterval(() => {
      const company = JSON.parse(localStorage.getItem("selectedCompany"));
      if (company?.id !== selectedCompany?.id) {
        setSelectedCompany(company);
        if (company?.id) {
          fetchInvoices(company.id);
        }
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [selectedCompany]);

  // Fetch company info based on CompID from localStorage
  useEffect(() => {
    const compId = localStorage.getItem("companyId");
    if (compId) {
      axios
        .get(`${BASE_API_URL}/invoice/company/${compId}`)
        .then((res) => {
          if (res.data.success) {
            setCompanyInfo(res.data.data);
            if (res.data.data?.CompanyCode) {
              const comp = JSON.parse(localStorage.getItem("selectedCompany")) || {};
              if (!comp.CompanyCode) {
                comp.CompanyCode = res.data.data.CompanyCode;
                localStorage.setItem("selectedCompany", JSON.stringify(comp));
              }
            }
          } else {
            showToast.error(res.data.message || "Company info not found");
          }
        })
        .catch(() => showToast.error("Failed to load company info"));
    }
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterFromDate, filterToDate, filterPartyName, invoices]);

  // Fetch invoices from backend
  const fetchInvoices = async (CompID) => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${BASE_API_URL}/invoice/get-invoices/${CompID}`
      );
      if (res.data.success) {
        setInvoices(res.data.data || []);
      } else {
        setInvoices([]);
        showToast.error("Failed to load invoices!");
      }
    } catch (err) {
      console.error("Error fetching invoices:", err);
      showToast.error("Error loading invoices!");
    } finally {
      setIsLoading(false);
    }
  };

  // Format date to DD-MM-YYYY
  const formatDate = (dateString) => {
    return formatDateToDMY(dateString);
  };

  // Navigate to detail page for invoice
  const handleView = (invoice) => {
    if (!selectedCompany?.id) {
      showToast.error("Please select a company first!");
      return;
    }
    navigate(`/invoice-details/${selectedCompany.id}/${invoice.InvoiceNo}`);
  };

  // ===== Handle Delete Invoice =====
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const CompID = localStorage.getItem("companyId") || selectedCompany?.id;
    if (!CompID) {
      showToast.error("Company not selected!");
      return;
    }
    setIsDeleting(true);
    try {
      const res = await axios.delete(
        `${BASE_API_URL}/invoice/delete/${CompID}?invoiceNo=${encodeURIComponent(deleteTarget.InvoiceNo)}`
      );
      if (res.data.success) {
        showToast.delete(`Invoice #${deleteTarget.InvoiceNo} deleted successfully!`);
        setInvoices((prev) =>
          prev.filter((inv) => inv.InvoiceNo !== deleteTarget.InvoiceNo)
        );
        setDeleteTarget(null);
      } else {
        showToast.error(res.data.message || "Failed to delete invoice");
      }
    } catch (err) {
      console.error("Error deleting invoice:", err);
      showToast.error("Error deleting invoice. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownload = async (invoice) => {
    try {
      await downloadInvoice(invoice);
    } catch (err) {
      console.error("PDF ERROR:", err);
      showToast.error("Failed to generate invoice PDF!");
    }
  };

  const handleGenerateVoucher = async (inv) => {
    const compId = selectedCompany?.id || localStorage.getItem("companyId");
    const userId = localStorage.getItem("userId") || 1;
    const invoiceId = inv.ID || inv.InvoiceID;

    if (!compId || !invoiceId) {
      showToast.error("Missing company or invoice information!");
      return;
    }

    try {
      showToast.info(`Generating vouchers for Invoice #${inv.InvoiceNo}...`);
      const res = await axios.post(`${BASE_API_URL}/vouchers/generate-from-invoice`, {
        compId: Number(compId),
        invoiceId: Number(invoiceId),
        userId: Number(userId),
      });

      if (res.data?.success) {
        showToast.success(res.data.message || "Vouchers posted successfully!");
        fetchInvoices();
      } else {
        showToast.error(res.data?.message || "Failed to generate vouchers");
      }
    } catch (err) {
      console.error("Voucher generation error:", err);
      showToast.error(err.response?.data?.message || "Error posting vouchers");
    }
  };

  const parties = Array.from(
    new Set(
      (invoices || [])
        .map((inv) => inv.PartyName)
        .filter((name) => name && String(name).trim() !== "")
    )
  ).sort();

  const filteredInvoices = (invoices || []).filter((inv) => {
    let invDate = "";
    if (inv.InvoiceDate) {
      const d = new Date(inv.InvoiceDate);
      if (!isNaN(d.getTime())) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const dt = String(d.getDate()).padStart(2, "0");
        invDate = `${y}-${m}-${dt}`;
      } else {
        invDate = String(inv.InvoiceDate).split("T")[0];
      }
    }
    if (filterFromDate && invDate && invDate < filterFromDate) return false;
    if (filterToDate && invDate && invDate > filterToDate) return false;
    if (filterPartyName && inv.PartyName !== filterPartyName) return false;
    return true;
  });

  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedInvoices = filteredInvoices.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100%",
        padding: "16px",
        backgroundColor: "#f4f6f8",
        boxSizing: "border-box",
        fontFamily: "Arial, Helvetica, sans-serif",
      }}
    >
      {/* TOP FILTER BAR */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "8px",
          padding: "6px 12px",
          background: "#fff",
          borderRadius: "6px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
          marginBottom: "8px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span
            style={{
              fontWeight: 600,
              fontSize: "11.5px",
              color: "#4a5568",
              whiteSpace: "nowrap",
            }}
          >
            From Date
          </span>
          <input
            type="date"
            value={filterFromDate}
            onChange={(e) => setFilterFromDate(e.target.value)}
            style={{
              height: "28px",
              width: "130px",
              padding: "2px 8px",
              borderRadius: "4px",
              border: "1px solid #cbd5e0",
              fontSize: "11.5px",
              background: "#fff",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span
            style={{
              fontWeight: 600,
              fontSize: "11.5px",
              color: "#4a5568",
              whiteSpace: "nowrap",
            }}
          >
            To Date
          </span>
          <input
            type="date"
            value={filterToDate}
            onChange={(e) => setFilterToDate(e.target.value)}
            style={{
              height: "28px",
              width: "130px",
              padding: "2px 8px",
              borderRadius: "4px",
              border: "1px solid #cbd5e0",
              fontSize: "11.5px",
              background: "#fff",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span
            style={{
              fontWeight: 600,
              fontSize: "11.5px",
              color: "#4a5568",
              whiteSpace: "nowrap",
            }}
          >
            Party / Company
          </span>
          <select
            value={filterPartyName}
            onChange={(e) => setFilterPartyName(e.target.value)}
            style={{
              height: "28px",
              minWidth: "180px",
              maxWidth: "260px",
              padding: "0 8px",
              borderRadius: "4px",
              border: "1px solid #cbd5e0",
              fontSize: "11.5px",
              background: "#fff",
              cursor: "pointer",
              outline: "none",
              boxSizing: "border-box",
            }}
          >
            <option value="">All Parties</option>
            {parties.map((p, i) => (
              <option key={i} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <button
            type="button"
            onClick={() => {
              setFilterFromDate(getFirstDayOfMonth());
              setFilterToDate(getTodayDate());
              setFilterPartyName("");
            }}
            title="Reset to current month"
            style={{
              height: "28px",
              padding: "0 14px",
              background: "#008b8b",
              color: "#ffffff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "11.5px",
              fontWeight: 600,
              whiteSpace: "nowrap",
              transition: "0.15s",
              boxShadow: "0 1px 2px rgba(0, 139, 139, 0.15)",
            }}
          >
            Reset
          </button>

          {(filterFromDate || filterToDate || filterPartyName) && (
            <button
              type="button"
              onClick={() => {
                setFilterFromDate("");
                setFilterToDate("");
                setFilterPartyName("");
              }}
              title="Clear all filters to view all invoices"
              style={{
                height: "28px",
                padding: "0 12px",
                background: "#f1f5f9",
                color: "#475569",
                border: "1px solid #cbd5e0",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "11.5px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                transition: "0.15s",
              }}
            >
              Clear All
            </button>
          )}
        </div>

        <Link
          to="/BillsInvoice"
          style={{
            marginLeft: "auto",
            height: "28px",
            padding: "0 14px",
            backgroundColor: "#008b8b",
            color: "#ffffff",
            borderRadius: "4px",
            fontWeight: 600,
            fontSize: "11.5px",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            textDecoration: "none",
            whiteSpace: "nowrap",
            boxShadow: "0 1px 2px rgba(0, 139, 139, 0.15)",
            boxSizing: "border-box",
          }}
        >
          + New Invoice
        </Link>
      </div>

      {/* LIST SECTION */}
      <div
        style={{
          background: "#fff",
          borderRadius: "8px",
          padding: "16px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <h6
          style={{
            fontSize: "16px",
            fontWeight: 700,
            color: "#1e293b",
            marginBottom: "14px",
          }}
        >
          Invoice List
        </h6>

        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "13px",
            }}
          >
            <thead>
              <tr style={{ background: "#008b8b", color: "#fff" }}>
                <th
                  style={{
                    padding: "8px 12px",
                    fontWeight: 700,
                    textAlign: "left",
                    borderRight: "1px solid rgba(255,255,255,0.2)",
                  }}
                >
                  Date
                </th>
                <th
                  style={{
                    padding: "8px 12px",
                    fontWeight: 700,
                    textAlign: "left",
                    borderRight: "1px solid rgba(255,255,255,0.2)",
                  }}
                >
                  Invoice No
                </th>
                <th
                  style={{
                    padding: "8px 12px",
                    fontWeight: 700,
                    textAlign: "left",
                    borderRight: "1px solid rgba(255,255,255,0.2)",
                  }}
                >
                  Party Name
                </th>
                <th
                  style={{
                    padding: "8px 12px",
                    fontWeight: 700,
                    textAlign: "left",
                    borderRight: "1px solid rgba(255,255,255,0.2)",
                  }}
                >
                  Total Amount
                </th>
                <th
                  style={{
                    padding: "8px 12px",
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <TableRowsSkeleton columns={5} rows={5} />
              ) : paginatedInvoices.length > 0 ? (
                paginatedInvoices.map((inv, index) => {
                  const getInvoiceDisplayTotal = (iObj) => {
                    if (!iObj) return 0;
                    return Number(iObj.BillTotal || iObj.GrandTotal || 0);
                  };

                  const displayTotal = getInvoiceDisplayTotal(inv);

                  return (
                    <tr
                      key={inv.ID || inv.InvoiceNo || index}
                      style={{
                        background: index % 2 === 0 ? "#ffffff" : "#f8fafc",
                        borderBottom: "1px solid #e2e8f0",
                        transition: "background 0.2s ease",
                      }}
                    >
                      <td style={{ padding: "10px 12px", border: "1px solid #e2e8f0" }}>
                        {formatDateToDMY(inv.InvoiceDate)}
                      </td>
                      <td
                        style={{
                          padding: "10px 12px",
                          border: "1px solid #e2e8f0",
                          fontWeight: 500,
                        }}
                      >
                        {formatInvoiceNumber(inv.InvoiceNo, inv.InvoiceDate, companyInfo?.CompanyCode || selectedCompany?.CompanyCode || "DDT")}
                      </td>
                      <td style={{ padding: "10px 12px", border: "1px solid #e2e8f0" }}>
                        {inv.PartyName}
                      </td>
                      <td
                        style={{
                          padding: "10px 12px",
                          border: "1px solid #e2e8f0",
                          fontWeight: 600,
                        }}
                      >
                        ₹{displayTotal.toLocaleString("en-IN")}
                      </td>
                      <td
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #e2e8f0",
                          textAlign: "center",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <div
                          style={{
                            display: "inline-flex",
                            gap: "6px",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => setPreviewModal({ open: true, data: inv })}
                            title="Preview Invoice"
                            style={{
                              width: "28px",
                              height: "28px",
                              borderRadius: "6px",
                              background: "#ffffff",
                              border: "1px solid #cbd5e1",
                              color: "#2563eb",
                              fontSize: "12.5px",
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
                              transition: "all 0.15s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "#2563eb";
                              e.currentTarget.style.color = "#ffffff";
                              e.currentTarget.style.borderColor = "#2563eb";
                              e.currentTarget.style.boxShadow = "0 2px 6px rgba(37, 99, 235, 0.25)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "#ffffff";
                              e.currentTarget.style.color = "#2563eb";
                              e.currentTarget.style.borderColor = "#cbd5e1";
                              e.currentTarget.style.boxShadow = "0 1px 2px rgba(0, 0, 0, 0.04)";
                            }}
                          >
                            <i className="fa-solid fa-eye"></i>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDownload(inv)}
                            title="Download Invoice PDF"
                            style={{
                              width: "28px",
                              height: "28px",
                              borderRadius: "6px",
                              background: "#ffffff",
                              border: "1px solid #cbd5e1",
                              color: "#059669",
                              fontSize: "12.5px",
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
                              transition: "all 0.15s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "#059669";
                              e.currentTarget.style.color = "#ffffff";
                              e.currentTarget.style.borderColor = "#059669";
                              e.currentTarget.style.boxShadow = "0 2px 6px rgba(5, 150, 105, 0.25)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "#ffffff";
                              e.currentTarget.style.color = "#059669";
                              e.currentTarget.style.borderColor = "#cbd5e1";
                              e.currentTarget.style.boxShadow = "0 1px 2px rgba(0, 0, 0, 0.04)";
                            }}
                          >
                            <i className="fa-solid fa-download"></i>
                          </button>
                          <button
                            type="button"
                            onClick={() => navigate(`/BillsInvoice?edit=${encodeURIComponent(inv.InvoiceNo)}`)}
                            title="Modify Invoice"
                            style={{
                              width: "28px",
                              height: "28px",
                              borderRadius: "6px",
                              background: "#ffffff",
                              border: "1px solid #cbd5e1",
                              color: "#f59e0b",
                              fontSize: "12.5px",
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
                              transition: "all 0.15s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "#f59e0b";
                              e.currentTarget.style.color = "#ffffff";
                              e.currentTarget.style.borderColor = "#f59e0b";
                              e.currentTarget.style.boxShadow = "0 2px 6px rgba(245, 158, 11, 0.25)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "#ffffff";
                              e.currentTarget.style.color = "#f59e0b";
                              e.currentTarget.style.borderColor = "#cbd5e1";
                              e.currentTarget.style.boxShadow = "0 1px 2px rgba(0, 0, 0, 0.04)";
                            }}
                          >
                            <i className="fa-solid fa-pen-to-square"></i>
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(inv)}
                            title="Delete Invoice"
                            style={{
                              width: "28px",
                              height: "28px",
                              borderRadius: "6px",
                              background: "#ffffff",
                              border: "1px solid #cbd5e1",
                              color: "#ef4444",
                              fontSize: "12.5px",
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
                              transition: "all 0.15s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "#ef4444";
                              e.currentTarget.style.color = "#ffffff";
                              e.currentTarget.style.borderColor = "#ef4444";
                              e.currentTarget.style.boxShadow = "0 2px 6px rgba(239, 68, 68, 0.25)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "#ffffff";
                              e.currentTarget.style.color = "#ef4444";
                              e.currentTarget.style.borderColor = "#cbd5e1";
                              e.currentTarget.style.boxShadow = "0 1px 2px rgba(0, 0, 0, 0.04)";
                            }}
                          >
                            <i className="fa-regular fa-trash-can"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    style={{
                      textAlign: "center",
                      padding: "24px",
                      color: "#64748b",
                    }}
                  >
                    No invoices found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION BAR */}
        {filteredInvoices.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "12px",
              padding: "12px 6px 4px",
              marginTop: "8px",
              borderTop: "1px solid #e2e8f0",
              fontSize: "13px",
              color: "#475569",
            }}
          >
            <div>
              Showing {startIndex + 1} to{" "}
              {Math.min(startIndex + itemsPerPage, filteredInvoices.length)} of{" "}
              {filteredInvoices.length} entries
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={{
                  padding: "5px 12px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  background: currentPage === 1 ? "#f1f5f9" : "#fff",
                  color: currentPage === 1 ? "#94a3b8" : "#334155",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  fontWeight: 500,
                  fontSize: "13px",
                }}
              >
                Previous
              </button>
              <span style={{ fontWeight: 600, padding: "0 6px" }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage >= totalPages}
                style={{
                  padding: "5px 12px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  background: currentPage >= totalPages ? "#f1f5f9" : "#fff",
                  color: currentPage >= totalPages ? "#94a3b8" : "#334155",
                  cursor: currentPage >= totalPages ? "not-allowed" : "pointer",
                  fontWeight: 500,
                  fontSize: "13px",
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Reusable Custom Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => !isDeleting && setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Invoice?"
        message="Are you sure you want to delete this invoice? This will free the invoice number for reuse."
        itemName={deleteTarget ? `Invoice #${deleteTarget.InvoiceNo}` : ""}
        loading={isDeleting}
      />

      {/* Invoice PDF Preview Modal */}
      <PdfPreviewModal
        isOpen={previewModal.open}
        type="invoice"
        data={previewModal.data}
        onClose={() => setPreviewModal({ open: false, data: null })}
      />
    </div>
  );
};

export default ViewInvoice;